import React, { Suspense, useEffect, useMemo, useState } from "react";
import {
  Table,
  Row,
  Col,
  Card,
  CardBody,
  Form,
  Modal,
  Label,
  Input,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import { useTable, useGlobalFilter, usePagination } from "react-table";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";

import "../../../assets/css/Finance/Style/Finance GST/financegst.css";

import Pagination from "components/Common/Pagination";
import {
  fetchNodeData,
  FetchNodeDeleteData,
  FetchNodePostData,
  FetchNodePutData,
} from "utils/common-fetchData";
import secureLocalStorage from "react-secure-storage";
import Loader from "pages/Separate/Loader";
import moment from "moment";
import configure from "configure";
import Select from "react-select";
import { colourStyles } from "utils/common-options";

const fmt = (val) => Number(val || 0).toFixed(2);

const isBetween = (date, start, end) => {
  const d = moment(date);
  if (start && end) return d.isBetween(moment(start), moment(end), null, "[]");
  if (start) return d.isSameOrAfter(moment(start));
  if (end) return d.isSameOrBefore(moment(end));
  return true;
};

function getCurrentMonthData(data) {
  return data.filter((item) =>
    moment(item.date, "YYYY-MM-DD").isSame(moment(), "month"),
  );
}

const types = {
  invoice: {
    label: "Invoice",
    color: "bg-success",
  },
  ["vendor pay"]: {
    label: "Vendor Pay",
    color: "bg-warning",
  },
  expense: {
    label: "Expense",
    color: "bg-primary",
  },
  ["Manual GST"]: {
    label: "Manual GST",
    color: "bg-secondary",
  },
};

function TableContainer({ columns, data, toggleGSTModal }) {
  const [fixedContent, setFixedContent] = useState(false);
  const [formPopup, setFormPopup] = useState(false);
  const [filteredData, setFilteredData] = useState(data);

  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    type: [],
  });
  const [activeFilter, setActiveFilter] = useState(false);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    state,
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
    gotoPage,
    setPageSize,
    pageOptions,
    setGlobalFilter,
    prepareRow,
  } = useTable(
    {
      columns,
      data: filteredData,
      initialState: {
        pageIndex: 0, // ✅ always reset to first page
        pageSize: 50,
      },
      autoResetPage: false, // ✅ prevent page reset on filter/search
    },
    useGlobalFilter,
    usePagination,
  );

  const { pageIndex, pageSize, globalFilter } = state;

  const navigate = useNavigate();

  useEffect(() => {
    setFilteredData(getCurrentMonthData(data));
  }, [data]);

  useEffect(() => {
    document.addEventListener("scroll", () => {
      if (window.scrollY > 226) setFixedContent(true);
      else setFixedContent(false);
    });
  }, []);

  const applyFilter = () => {
    let temp = [
      ...(filters.startDate && filters.endDate
        ? data
        : getCurrentMonthData(data)),
    ];
    // DATE FILTER
    if (filters.startDate || filters.endDate) {
      temp = temp.filter((d) =>
        isBetween(d.date, filters.startDate, filters.endDate),
      );
    }

    if (filters.type?.length) {
      const selectedTypes = filters.type.map((i) => i.value);

      temp = temp.filter((d) => {
        // invoice = Debit
        if (selectedTypes.includes("Debit") && d.type === "invoice")
          return true;

        // expense & vendor pay = Credit
        if (selectedTypes.includes("Credit") && d.type !== "invoice")
          return true;

        return false;
      });
    }

    gotoPage(0);

    setFilteredData(temp);
    setActiveFilter(true);
    // setFormPopup(false);
  };

  const clearFilter = () => {
    setFilters({ startDate: "", endDate: "", type: [] });
    setFilteredData(getCurrentMonthData(data));
    // setFormPopup(false);
    gotoPage(0);
    setActiveFilter(false);
  };

  const exportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(
      filteredData?.map((item) => ({
        Date: moment(item.date).format("DD-MM-YYYY"),
        Title: item.title,
        ["Invoice Number"]: item.invoiceNo,
        ["Customer / Project"]:
          item.customer?.replace("--", "") || item.project?.replace("--", ""),
        Category: item.category,
        Type: item.type,
        Debit: item.debit,
        Credit: item.credit,
      })),
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "GST");
    XLSX.writeFile(wb, "gst_data.xlsx");
  };

  // ── Balance calculations ────────────────────────────────────────────────────
  const calcBalance = (rows) => {
    let credit = 0,
      debit = 0;
    rows.forEach((item) => {
      if (["invoice", "Manual GST"].includes(item.type) && item.debit !== "--")
        debit += Number(item.debit || 0);
      else if (item.credit !== "--") credit += Number(item.credit || 0);
    });
    return { credit, debit, net: credit - debit };
  };

  const currentMonthBalance = useMemo(() => {
    const rows = data.filter((d) => moment(d.date).isSame(moment(), "month"));

    return calcBalance(rows);
  }, [data]);

  const previousMonthBalance = useMemo(() => {
    const rows = data.filter((d) =>
      moment(d.date).isSame(moment().subtract(1, "month"), "month"),
    );

    return calcBalance(rows);
  }, [data]);

  // ── Balance Card ────────────────────────────────────────────────────────────
  const BalanceCard = ({ label, balance }) => (
    <div className="budget_status py-2 px-3 rounded proposal-status text-center w-auto">
      <p className="mb-1 text-muted fw-bold" style={{ fontSize: 12 }}>
        {label}
      </p>
      <div className="d-flex gap-3 justify-content-center">
        <small className="text-success">
          <i className="fas fa-arrow-up me-1" style={{ fontSize: 10 }} />₹
          {fmt(balance.credit)}
        </small>
        <small className="text-danger">
          <i className="fas fa-arrow-down me-1" style={{ fontSize: 10 }} />₹
          {fmt(balance.debit)}
        </small>
      </div>
      <h5
        className={`mb-0 mt-1 ${
          balance.net >= 0 ? "text-success" : "text-danger"
        }`}
      >
        ₹ {fmt(Math.abs(balance.net))}
        <small style={{ fontSize: 11, marginLeft: 4 }}>
          {balance.net >= 0 ? "(Net Credit)" : "(Net Debit)"}
        </small>
      </h5>
    </div>
  );

  return (
    <div>
      {/* fixed buttons */}
      <div
        className={`flex-column fixed-buttons ${
          fixedContent ? "d-flex" : "d-none"
        }`}
      >
        <button
          onClick={exportExcel}
          className="btn btn-success border-radius fix-btn btn-label"
        >
          <i className="fas fa-download me-2 icon-center label-icon"></i>
          <span>Export</span>
        </button>

        <button
          onClick={toggleGSTModal}
          className="btn btn-purple border-radius fix-btn btn-label"
        >
          <i className="fas fa-plus me-2 icon-center label-icon"></i>
          <span>GST</span>
        </button>

        <button
          onClick={() => navigate(-1)}
          className="btn btn-red border-radius fix-btn btn-label"
        >
          <i className="dripicons-cross  me-2 icon-center label-icon"></i>
          <span>Cancel</span>
        </button>
      </div>

      <Row className="mb-2 align-items-start">
        <Col>
          <div className="d-flex gap-3 mb-4">
            <BalanceCard
              label="Current Month Balance"
              balance={currentMonthBalance}
            />

            <BalanceCard
              label="Previous Month Balance"
              balance={previousMonthBalance}
            />
          </div>
        </Col>

        <Col md={8} className="d-flex gap-2 justify-content-end">
          <div className="me-2">
            <select
              className="border-radius form-select"
              style={{ width: "100px" }}
              value={pageSize}
              onChange={(e) => setPageSize(e.target.value)}
            >
              {[10, 20, 30, 40, 50].map((size) => (
                <option key={size} value={size}>
                  Show {size}
                </option>
              ))}
            </select>
          </div>
          <div className="me-2">
            <div className="search-box d-inline-block">
              <div className="position-relative">
                <input
                  type="text"
                  className="border-radius form-control"
                  placeholder="Search..."
                  value={globalFilter || ""}
                  onChange={(e) => setGlobalFilter(e.target.value)}
                />
                <i className="bx bx-search-alt search-icon" />
              </div>
            </div>
          </div>

          <button
            onClick={() => setFormPopup(!formPopup)}
            className={`btn ${
              activeFilter ? "btn-success" : "btn-outline-secondary"
            } me-2`}
          >
            <i className="fas fa-filter"></i>{" "}
            {activeFilter && "Applied!"}
          </button>

          <button
            onClick={exportExcel}
            className="border-radius btn text-white me-2 btn-success btn-label"
          >
            <i className="fas fa-download me-2 icon-size label-icon"></i> Export
          </button>

          <button
            onClick={toggleGSTModal}
            className="border-radius btn text-white me-2 btn-purple btn-label"
          >
            <i className="fas fa-plus me-2 icon-size label-icon"></i> GST
          </button>

          <button
            onClick={() => navigate(-1)}
            className="border-radius btn btn-red btn-label"
          >
            <i className="dripicons-cross label-icon me-2 icon-center"></i>
            CANCEL
          </button>
        </Col>
      </Row>

      {/* FILTER POPUP */}
      {formPopup && (
        <div className="p-4 mb-3 rounded border bg-light">
          <Form>
            <Row className="align-items-end">
              <div className="col-md-2">
                <label>Start Date</label>
                <input
                  type="date"
                  className="form-control "
                  value={filters.startDate}
                  onChange={(e) =>
                    setFilters({ ...filters, startDate: e.target.value })
                  }
                />
              </div>

              <div className="col-md-2">
                <label>End Date</label>
                <input
                  type="date"
                  className="form-control "
                  value={filters.endDate}
                  onChange={(e) =>
                    setFilters({ ...filters, endDate: e.target.value })
                  }
                />
              </div>

              <div className="col-md-4">
                <label>By</label>
                <Select
                  isMulti
                  className="basic-multi-select"
                  classNamePrefix=" select"
                  placeholder="--- Please Select ---"
                  value={filters.type}
                  styles={colourStyles}
                  options={[
                    {
                      value: "Debit",
                      label: "Debit (Invoice)",
                    },
                    {
                      value: "Credit",
                      label: "Credit (Expense / Vendor Pay)",
                    },
                  ]}
                  onChange={(e) => setFilters({ ...filters, type: e })}
                />

                {/* <select
                  className="form-select border-radius"
                  value={filters.type}
                  onChange={(e) =>
                    setFilters({ ...filters, type: e.target.value })
                  }
                >
                  <option>Nothing Selected</option>
                  <option value="Debit">Debit (Invoice)</option>
                  <option value="Credit">Credit (Expense / Vendor Pay)</option>
                </select> */}
              </div>

              <div className="text-center col-md-4">
                <button
                  className="btn border-radius btn-purple me-3"
                  onClick={(e) => {
                    e.preventDefault();
                    applyFilter();
                  }}
                >
                  <i className="fas fa-search me-2"></i> Search
                </button>

                <button
                  className="btn border-radius btn-warning me-3"
                  onClick={(e) => {
                    e.preventDefault();
                    clearFilter();
                  }}
                >
                  Clear
                </button>
                <button
                  className="btn border-radius btn-red "
                  onClick={(e) => {
                    e.preventDefault();
                    setFormPopup(false);
                  }}
                >
                  <i className="dripicons-cross me-1"></i> Close
                </button>
              </div>
            </Row>
          </Form>
        </div>
      )}

      {/* TABLE */}
      <Table className="table table-bordered mt-3" {...getTableProps()}>
        <thead>
          {headerGroups.map((hg, i) => (
            <tr key={i} {...hg.getHeaderGroupProps()}>
              {hg.headers.map((col, j) => (
                <th key={j} {...col.getHeaderProps()}>
                  {col.render("Header")}
                </th>
              ))}
            </tr>
          ))}
        </thead>

        <tbody {...getTableBodyProps()}>
          {page.map((row, i) => {
            prepareRow(row);
            return (
              <tr key={i} {...row.getRowProps()}>
                {row.cells.map((cell, j) => (
                  <td key={j} {...cell.getCellProps()}>
                    {cell.render("Cell")}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </Table>

      {/* PAGINATION */}
      <Row className="justify-content-md-end">
        <Pagination
          gotoPage={gotoPage}
          previousPage={previousPage}
          canPreviousPage={canPreviousPage}
          nextPage={nextPage}
          canNextPage={canNextPage}
          pageIndex={pageIndex}
          pageOptions={pageOptions}
        />
      </Row>
    </div>
  );
}

const ZithasGST = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [gstModal, setGstModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [categoryOptions, setCategoryOptions] = useState([]);

  const [gstForm, setGstForm] = useState({
    title: "",
    date: moment().format("YYYY-MM-DD"),
    type: "credit",
    amount: "",
    category: "",
  });

  const toggleGSTModal = () => {
    setGstModal(!gstModal);

    if (gstModal) {
      setEditId(null);

      setGstForm({
        title: "",
        date: moment().format("YYYY-MM-DD"),
        type: "credit",
        amount: "",
        category: "",
      });
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      const token = secureLocalStorage.getItem("auth_token");

      const categories = await fetchNodeData(false, "financeCategory", token);

      setCategoryOptions(categories?.data || []);

      const res = await fetchNodeData(
        false,
        `gst/${configure.location[0]}`,
        token,
      );

      if (res.success) {
        const formatted = res.data
          .map((item) => ({
            id: item.id,
            date: item.date,
            title: item.title,
            invoiceNo: item?.tax_inv_no || "--",
            customer: item?.customer?.company || "--",
            project: item.project || "--",
            category: ["expense", "credit", "debit"].includes(item.type)
              ? categories?.data?.find((i) => i.id == item.category)?.name
              : item.type === "vendor pay"
              ? item.category
              : "--",
            categoryId: item.category || null,
            type: item.type,
            manual_type: item.type,
            debit: ["invoice", "debit"].includes(item.type)
              ? Number(item.gstValue).toFixed(2)
              : "--",
            credit: ["expense", "vendor pay", "credit"].includes(item.type)
              ? Number(item.gstValue).toFixed(2)
              : "--",
            ...(["credit", "debit"].includes(item.type)
              ? { isManual: true, type: "Manual GST" }
              : {}),
          }))
          .sort(
            (a, b) =>
              moment(b.date, "YYYY-MM-DD").valueOf() -
              moment(a.date, "YYYY-MM-DD").valueOf(),
          );

        setData(formatted);
      }

      setLoading(false);
    })();
  }, []);

  // functions
  const saveGST = async () => {
    try {
      if (!gstForm.title || !gstForm.date || !gstForm.type || !gstForm.amount) {
        return swal("", "Please fill all fields", "warning");
      }

      swal({
        title: "Are you sure?",
        text: `You want to ${editId ? "edit" : "add"} gst record!`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      }).then(async (willProceed) => {
        if (willProceed) {
          try {
            const payload = {
              title: gstForm.title,
              date: gstForm.date,
              type: gstForm.type,
              amount: gstForm.amount,
              category: gstForm.category,
              location: configure.location[0],
              added_by: localStorage.getItem("auth_id"),
            };

            let res;

            if (editId) {
              res = await FetchNodePutData(
                setLoading,
                `gst/manual-gst/update`,
                editId,
                payload,
              );
            } else {
              res = await FetchNodePostData(
                setLoading,
                "gst/manual-gst/create",
                secureLocalStorage.getItem("auth_token"),
                payload,
              );
            }

            if (res.data.success) {
              const newItem = {
                id: editId || res.data?.data?.id,
                date: gstForm.date,
                title: gstForm.title,
                invoiceNo: "--",
                customer: "--",
                category: categoryOptions?.find((i) => i.id == gstForm.category)
                  ?.name,
                categoryId: gstForm.category || null,
                project: "--",
                type: "Manual GST",
                manual_type: gstForm.type,
                isManual: true,
                debit:
                  gstForm.type === "debit"
                    ? Number(gstForm?.amount)?.toFixed(2)
                    : "--",
                credit:
                  gstForm.type === "credit"
                    ? Number(gstForm?.amount)?.toFixed(2)
                    : "--",
              };

              if (editId) {
                setData((prev) =>
                  prev
                    .map((i) =>
                      Number(i.id) === Number(editId) && i.type == "Manual GST"
                        ? newItem
                        : i,
                    )
                    .sort(
                      (a, b) =>
                        moment(b.date).valueOf() - moment(a.date).valueOf(),
                    ),
                );
              } else {
                setData((prev) =>
                  [newItem, ...prev].sort(
                    (a, b) =>
                      moment(b.date).valueOf() - moment(a.date).valueOf(),
                  ),
                );
              }

              toggleGSTModal();

              swal("Done", "GST Record Added Successfully", "success");
            }
          } catch (error) {
            console.error("TDS submit error:", error);
          }
        }
      });
    } catch (err) {
      console.log(err);
    }
  };

  const handleEdit = (row) => {
    setEditId(row.id);

    setGstForm({
      title: row.title,
      date: row.date,
      type: row.manual_type,
      category: row.categoryId,
      amount: Number(
        String(row.debit)?.replace("--", "") ||
          String(row.credit)?.replace("--", ""),
      ),
    });

    setGstModal(true);
  };

  const handleDelete = async (id) => {
    swal({
      title: "Are you sure?",
      text: `You want to delete this GST record.`,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (willProceed) => {
      if (willProceed) {
        try {
          const res = await FetchNodeDeleteData(
            false,
            `gst/manual-gst/delete`,
            id,
          );

          if (res.data.success) {
            setData((prev) => prev.filter((i) => i.id !== id));
            swal(
              "Deleted Successfully",
              "GST Record Deleted Successfully",
              "success",
            );
          }
        } catch (err) {
          console.log(err);
        }
      }
    });
  };

  const columns = useMemo(
    () => [
      { Header: "ID", accessor: "id" },
      {
        Header: "Date",
        accessor: "date",
        Cell: (props) => (
          <span>{moment(props.row.original.date).format("DD-MM-YYYY")}</span>
        ),
      },
      { Header: "Title", accessor: "title" },
      { Header: "Invoice No", accessor: "invoiceNo" },
      {
        Header: "Customer / Project",
        accessor: "customer",
        className: "text-start",
        Cell: ({ row }) => {
          const company = row.original.customer;
          const project = row.original.project;
          return (
            <div>
              {company && company !== "--" ? (
                <div className="fw-semibold" style={{ fontSize: 13 }}>
                  {company}
                </div>
              ) : project && project !== "--" ? (
                <div className="text-muted">{project}</div>
              ) : (
                "--"
              )}
            </div>
          );
        },
      },
      { Header: "Category", accessor: "category" },
      {
        Header: "Type",
        accessor: "type",
        className: "text-center",
        Cell: ({ row }) => {
          const type = row.original.type;

          return (
            <span className={`badge ${types[type]?.color}`}>
              {types[type]?.label || "--"}
            </span>
          );
        },
      },
      {
        Header: () => <span className="text-danger col-1">{"Debit (-)"}</span>,
        accessor: "debit",
        Cell: ({ row }) =>
          row.original.debit !== "--" ? (
            <span className="text-danger fw-semibold">
              ₹ {row.original.debit}
            </span>
          ) : (
            <span className="text-muted">--</span>
          ),
      },
      {
        Header: () => (
          <span className="text-success col-1">{"Credit (+)"}</span>
        ),
        accessor: "credit",
        Cell: ({ row }) =>
          row.original.credit !== "--" ? (
            <span className="text-success fw-semibold">
              ₹ {row.original.credit}
            </span>
          ) : (
            <span className="text-muted">--</span>
          ),
      },
      {
        Header: "Action",
        accessor: "action",
        className: "text-center",

        Cell: ({ row }) => {
          if (!row.original.isManual) {
            return <span className="text-muted">--</span>;
          }

          return (
            <div className="d-flex gap-2 justify-content-center">
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() => handleEdit(row.original)}
              >
                <i className="fas fa-pen"></i>
              </button>

              <button
                className="btn btn-sm btn-danger"
                onClick={() => handleDelete(row.original.id)}
              >
                <i className="fas fa-trash"></i>
              </button>
            </div>
          );
        },
      },
    ],
    [],
  );

  return (
    <>
      {loading && <Loader />}
      <Row className="ms-0 pe-0">
        <Col lg={12} className="p-0">
          <Card>
            <CardBody>
              <TableContainer
                columns={columns}
                data={data}
                toggleGSTModal={toggleGSTModal}
              />
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Add/Edit GST  */}
      <Modal isOpen={gstModal} toggle={toggleGSTModal} centered size="md">
        <ModalHeader toggle={toggleGSTModal}>
          {editId ? "Edit GST Record" : "Add GST Record"}
        </ModalHeader>

        <ModalBody>
          <Row>
            <Col md={12} className="mb-3">
              <Label className="mb-1">
                Title <span className="text-danger">*</span>{" "}
              </Label>

              <Input
                type="text"
                className="form-control"
                placeholder="Enter GST title"
                value={gstForm.title}
                onChange={(e) =>
                  setGstForm({
                    ...gstForm,
                    title: e.target.value,
                  })
                }
              />
            </Col>

            <Col md={6} className="mb-3">
              <Label className="mb-1">
                Date <span className="text-danger">*</span>
              </Label>

              <Input
                type="date"
                className="form-control"
                value={gstForm.date}
                onChange={(e) =>
                  setGstForm({
                    ...gstForm,
                    date: e.target.value,
                  })
                }
              />
            </Col>

            <Col md={6} className="mb-3">
              <Label className="mb-1">
                Type <span className="text-danger">*</span>
              </Label>

              <Input
                type="select"
                className="form-select"
                value={gstForm.type}
                onChange={(e) =>
                  setGstForm({
                    ...gstForm,
                    type: e.target.value,
                  })
                }
              >
                <option value="">--- Select ---</option>

                <option value="debit">Debit</option>

                <option value="credit">Credit</option>
              </Input>
            </Col>

            <Col md={6} className="mb-3">
              <Label className="mb-1">
                Category <span className="text-danger">*</span>
              </Label>

              <Input
                type="select"
                className="form-select"
                value={gstForm.category}
                onChange={(e) =>
                  setGstForm({
                    ...gstForm,
                    category: e.target.value,
                  })
                }
              >
                <option value="">--- Select ---</option>
                {categoryOptions?.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </Input>
            </Col>

            <Col md={6}>
              <Label className="mb-1">
                Amount <span className="text-danger">*</span>
              </Label>

              <Input
                type="number"
                className="form-control"
                placeholder="Enter GST Amount"
                value={gstForm.amount}
                onChange={(e) =>
                  setGstForm({
                    ...gstForm,
                    amount: e.target.value,
                  })
                }
              />
            </Col>
          </Row>
        </ModalBody>

        <ModalFooter>
          <button
            className="btn btn-red border-radius"
            onClick={toggleGSTModal}
          >
            Cancel
          </button>

          <button
            className="btn btn-purple border-radius"
            onClick={saveGST}
            disabled={loading}
          >
            {loading ? (
              <>
                <i className="fas fa-spinner fa-spin me-2"></i>
                Saving...
              </>
            ) : (
              <>
                <i className="fas fa-save me-2"></i>
                {editId ? "Update GST" : "Save GST"}
              </>
            )}
          </button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default ZithasGST;
